/** 导航与屏注册表。
 *
 * 分组按「人一天怎么用它」而不是按模块（看盘 → 交易与账 → 风控 → 证据 → 验证 →
 * 通道 → 设置）——分组与屏名都照抄原型 `docs/design/ui-prototype/app.js` 的 `NAV`，
 * 路由命名保持一致（`#overview` / `#orders?id=…`）。
 *
 * `wired` = 前端屏已实现且它调用的端点在后端真有路由、形状对得上；`pending-backend` = 前端屏在
 * 但端点缺 / 形状不一致，或后端有而前端未接；`planned-card` = 卡未实现且前端未做。
 * 未接的屏路由仍在，页面上写清「接口未提供」以及对应哪张 Should 卡——**不允许**拿原型数据冒充真数据。
 * 判据与逐屏核对见 `docs/design/操作台对接开发文档.md` §10（campaign #4591，D0）。
 */

export type WireState = "wired" | "pending-backend" | "planned-card" | "candidate";

export interface NavItem {
  /** 路由 id（原型里的 `#id`）。 */
  id: string;
  label: string;
  wire: WireState;
  /** 未接时的说明：接口叫什么、等哪张卡。 */
  why?: string;
}

export interface NavGroup {
  group: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    group: "总览",
    items: [
      { id: "overview", label: "当日总览", wire: "wired" },
      { id: "halt", label: "停机停手", wire: "wired" },
      { id: "reports", label: "每日报告", wire: "wired" },
    ],
  },
  {
    group: "交易与账",
    items: [
      { id: "orders", label: "订单", wire: "wired" },
      { id: "fills", label: "成交", wire: "wired" },
      { id: "positions", label: "持仓", wire: "wired" },
      { id: "attribution", label: "逐单归因", wire: "wired" },
      { id: "approvals", label: "人工放行", wire: "wired" },
    ],
  },
  {
    group: "风控与对账",
    items: [
      { id: "reconciliation", label: "对账", wire: "wired" },
      { id: "parity", label: "三处同源比对", wire: "wired" },
      { id: "risk", label: "三层限额", wire: "wired" },
      { id: "riskreport", label: "风险核对报告", wire: "wired" },
    ],
  },
  {
    group: "信号与证据",
    items: [
      { id: "inbox", label: "信号收件箱", wire: "wired", why: "GET /signals、/signals/inbox（收件形状由后端统一成 ref，B9）、/paper/events、/data/mirror lines[] 已接；按线按日结论 GET /chain/daily 未提供时结论列标「缺」" },
      { id: "rejected", label: "拒收台账", wire: "wired", why: "GET /signals/rejections（桥接 / 运行 / 常驻投递三环节汇总）已接；通报字段无实现，整列标「缺」" },
      { id: "snapshots", label: "数据快照", wire: "wired", why: "GET /snapshots、/snapshots/{id}/verify、/data/daily(/{date})、/alerts、/data/mirror 已接" },
      { id: "raw-data", label: "原始输入", wire: "wired", why: "GET /data-snapshot、/store/verify 已接；库查询（/store/status、/books、/market/bars）需服务配 SOMEO_KEEL_DB_URL，未接库时 501「只读端点未接数据源」" },
      { id: "changelog", label: "变更台账", wire: "pending-backend", why: "前端屏已接 GET /changes，后端如实 501「只读端点未接数据源」：变更记录的取数来源待裁决（S8 change-validation「变更台账」，卡为 planned）" },
    ],
  },
  {
    group: "持续验证",
    items: [
      { id: "validation", label: "走查与失效判据", wire: "wired" },
      { id: "changereview", label: "变更验收", wire: "planned-card", why: "对应 S8 change-validation「基准跑 / 差异判读 / 结果留存」，卡为 planned：GET /changes/{change_id}/review 如实 501「只读端点未接数据源」，列表 GET /changes 归 changelog；屏已接，显示「接口未提供」" },
      { id: "analytics", label: "绩效分析", wire: "wired", why: "历史回测读 GET /validation/backtest（someo-backtrader 修正版，keel 算式重算）；归因报告 GET /attribution/report 四层对应表未定，四层一律标「缺」（B8）" },
      { id: "replay", label: "运行与回放", wire: "wired" },
    ],
  },
  {
    group: "通道与降级",
    items: [
      { id: "channels", label: "通道管理", wire: "wired" },
      { id: "degradation", label: "降级记录", wire: "wired" },
    ],
  },
  {
    group: "运行",
    items: [
      { id: "strategy", label: "策略与参数", wire: "wired" },
      { id: "logs", label: "日志", wire: "wired" },
      { id: "credentials", label: "凭据", wire: "candidate", why: "候选能力（未开卡）" },
      { id: "settings", label: "设置", wire: "wired" },
      { id: "login", label: "登录 / 账户", wire: "candidate", why: "候选能力（未开卡）" },
    ],
  },
];

/** 屏 id → 条目，供顶栏与未接线屏查说明。 */
export const NAV_INDEX: Map<string, NavItem> = new Map(
  NAV.flatMap((group) => group.items.map((item) => [item.id, item] as const)),
);
